import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiBarChart2,
  FiDollarSign,
  FiRepeat,
  FiShield,
  FiTrendingUp,
  FiUserCheck,
  FiXCircle,
} from "react-icons/fi";
import DashboardHeader from "./DashboardHeader";
import KpiGrid from "./KpiGrid";
import AlertsPanel from "./AlertsPanel";
import ActivityFeed from "./ActivityFeed";
import PaymentsSidebar from "./PaymentsSidebar";
import InvoicesTable from "./InvoicesTable";
import InvoicePaymentsTable from "./InvoicePaymentsTable";
import PlansTable from "./PlansTable";
import CustomersTable from "./CustomersTable";
import PlaceholderPanel from "./PlaceholderPanel";
import SubscriptionsTable from "./SubscriptionsTable";
import SubscriptionInvoicesTable from "./SubscriptionInvoicesTable";
import CreateInvoicePanel from "./CreateInvoicePanel";
import CreatePlanPanel from "./CreatePlanPanel";
import CreateSubscriptionPanel from "./CreateSubscriptionPanel";
import SubscriptionCheckoutPanel from "./SubscriptionCheckoutPanel";
import StripeCheckoutTable from "./StripeCheckoutTable";
import DisputesTable from "./DisputesTable";
import AnalyticsOverviewPanel from "./AnalyticsOverviewPanel";
import ReportChartCard from "./ReportChartCard";
import ReportInvoicesPanel from "./ReportInvoicesPanel";
import ReportSubscriptionsPanel from "./ReportSubscriptionsPanel";
import ConfirmDialog from "./ConfirmDialog";
import ErrorState from "./ErrorState";
import LoadingState from "./LoadingState";
import PaymentsHelpPanel from "./PaymentsHelpPanel";
import RefundDialog from "./RefundDialog";
import CheckoutLinkDialog from "./CheckoutLinkDialog";
import DisputeResponseDialog from "./DisputeResponseDialog";
import { AUTH_PROVIDERS, useAuthProvider } from "../Authentication/AuthProviderSwitch";
import { getConfigValue } from "../../config/runtimeConfig";
import { SignOutButton, UserProfile } from "../ClerkAuthentication/clerkReact";
import {
  cancelSubscription,
  createInvoice,
  createCheckoutSession,
  createSubscriptionCheckoutSession,
  createPlan,
  createSubscription,
  deletePlan,
  getAnalyticsOverview,
  getInvoices,
  getPaymentsHistory,
  getPlans,
  getDisputes,
  refundPayment,
  submitDisputeEvidence,
  getSubscriptionInvoices,
  getSubscriptions,
  setPaymentsApiTokenProvider,
} from "../../services/paymentsApi";
import { buildDashboardData, getTimestamp } from "../../services/paymentsDashboardMapper";

const statusColor = (status) => {
  switch (status) {
    case "SUCCESS":
    case "PAID":
    case "ACTIVE":
      return "green";
    case "PENDING":
      return "yellow";
    case "REFUNDED":
    case "CANCELED":
    case "DISPUTED":
      return "orange";
    case "FAILED":
      return "red";
    default:
      return "gray";
  }
};

const formatCurrency = (amount, currency = "USD") => {
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(value);
  } catch (error) {
    return `${currency} ${value.toFixed(2)}`;
  }
};

const formatDateTime = (value) => {
  if (!value) return "-";
  if (typeof value === "number") {
    return new Date(value).toLocaleString();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "-" : parsed.toLocaleString();
};

const rangeOptions = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  all: null,
};

const DAY_MS = 24 * 60 * 60 * 1000;

const getBucketCount = (rangeDays) => {
  if (!rangeDays) return 12;
  if (rangeDays <= 7) return 7;
  if (rangeDays <= 30) return 10;
  if (rangeDays <= 90) return 12;
  return 12;
};

const buildSeriesFromPoints = (points, rangeDays) => {
  const now = Date.now();
  let start = now - (rangeDays ? rangeDays * DAY_MS : 365 * DAY_MS);
  if (!rangeDays && points.length > 0) {
    const timestamps = points.map((point) => point.timestamp).filter(Boolean);
    const minTimestamp = Math.min(...timestamps);
    if (Number.isFinite(minTimestamp)) {
      start = minTimestamp;
    }
  }
  const bucketCount = getBucketCount(rangeDays);
  const totalRange = Math.max(1, now - start);
  const bucketSize = totalRange / bucketCount;
  const series = Array(bucketCount).fill(0);

  points.forEach((point) => {
    const timestamp = point.timestamp;
    if (!timestamp || timestamp < start || timestamp > now) return;
    const index = Math.min(bucketCount - 1, Math.floor((timestamp - start) / bucketSize));
    series[index] += Number(point.value || 0);
  });

  return series;
};

const padTwo = (value) => String(value).padStart(2, "0");

const toDateTimeInputValue = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = padTwo(date.getMonth() + 1);
  const day = padTwo(date.getDate());
  const hours = padTwo(date.getHours());
  const minutes = padTwo(date.getMinutes());
  const seconds = padTwo(date.getSeconds());
  const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
};

const toIsoString = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const buildAnalyticsPayload = (filters) => {
  if (!filters) return {};
  const payload = {};
  const startDate = toIsoString(filters.startDate);
  const endDate = toIsoString(filters.endDate);
  if (startDate) payload.startDate = startDate;
  if (endDate) payload.endDate = endDate;
  payload.includeTimeSeries = Boolean(filters.includeTimeSeries);
  if (filters.includeTimeSeries && filters.timeSeriesGranularity) {
    payload.timeSeriesGranularity = filters.timeSeriesGranularity;
  }
  return payload;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const PaymentsDashboardPage = () => {
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { provider, getToken, isAuthenticated } = useAuthProvider();
  useEffect(() => {
    if (!getToken || !isAuthenticated) {
      setPaymentsApiTokenProvider(null);
      return;
    }
    setPaymentsApiTokenProvider(() => getToken());
    return () => setPaymentsApiTokenProvider(null);
  }, [getToken, isAuthenticated]);
  const checkoutSuccessUrl =
    getConfigValue(
      "REACT_APP_PAYMENTS_SUCCESS_URL",
      process.env.REACT_APP_PAYMENTS_SUCCESS_URL,
      "https://teliviit.com/payments/success"
    );
  const checkoutCancelUrl =
    getConfigValue(
      "REACT_APP_PAYMENTS_CANCEL_URL",
      process.env.REACT_APP_PAYMENTS_CANCEL_URL,
      "https://teliviit.com/payments/cancel"
    );
  const [searchValue, setSearchValue] = useState("");
  const [rangeValue, setRangeValue] = useState("30d");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [invoicesData, setInvoicesData] = useState([]);
  const [invoicesAvailable, setInvoicesAvailable] = useState(false);
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionInvoices, setSubscriptionInvoices] = useState([]);

  const [plans, setPlans] = useState([]);
  const [plansAvailable, setPlansAvailable] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansAttempted, setPlansAttempted] = useState(false);
  const [disputes, setDisputes] = useState([]);
  const [disputesLoading, setDisputesLoading] = useState(false);
  const [disputesAttempted, setDisputesAttempted] = useState(false);
  const [disputesError, setDisputesError] = useState("");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");
  const [analyticsFilters, setAnalyticsFilters] = useState(() => {
    const end = new Date();
    const start = new Date(end.getTime() - 30 * DAY_MS);
    return {
      startDate: toDateTimeInputValue(start),
      endDate: toDateTimeInputValue(end),
      includeTimeSeries: true,
      timeSeriesGranularity: "DAY",
    };
  });
  const [analyticsDraftFilters, setAnalyticsDraftFilters] = useState(() => {
    const end = new Date();
    const start = new Date(end.getTime() - 30 * DAY_MS);
    return {
      startDate: toDateTimeInputValue(start),
      endDate: toDateTimeInputValue(end),
      includeTimeSeries: true,
      timeSeriesGranularity: "DAY",
    };
  });
  const [reportInvoiceStatus, setReportInvoiceStatus] = useState("all");
  const [reportSubscriptionStatus, setReportSubscriptionStatus] = useState("all");

  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [refundError, setRefundError] = useState(null);
  const [isDeletingPlan, setIsDeletingPlan] = useState(false);
  const [isGeneratingCheckout, setIsGeneratingCheckout] = useState(false);
  const [isSendingCheckout, setIsSendingCheckout] = useState(false);
  const [isSubmittingDispute, setIsSubmittingDispute] = useState(false);

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [checkoutSession, setCheckoutSession] = useState(null);
  const [checkoutTarget, setCheckoutTarget] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null);

  const cancelDialog = useDisclosure();
  const deletePlanDialog = useDisclosure();
  const refundDialog = useDisclosure();
  const checkoutDialog = useDisclosure();
  const disputeDialog = useDisclosure();
  const currentPath = location.pathname;
  const isClerkProvider = provider === AUTH_PROVIDERS.CLERK;
  const isOverview = currentPath === "/payments" || currentPath.startsWith("/payments/overview");
  const isProfileRoute = currentPath.startsWith("/payments/profile");
  const isReports = currentPath.startsWith("/payments/reports");
  const isAnalytics = currentPath.startsWith("/payments/analytics");

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const [paymentsData, subscriptionsData, invoicesData] = await Promise.all([
        getPaymentsHistory(),
        getSubscriptions(),
        getSubscriptionInvoices(),
      ]);
      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      setSubscriptions(Array.isArray(subscriptionsData) ? subscriptionsData : []);
      setSubscriptionInvoices(Array.isArray(invoicesData) ? invoicesData : []);
      try {
        const invoiceRows = await getInvoices();
        setInvoicesData(Array.isArray(invoiceRows) ? invoiceRows : []);
        setInvoicesAvailable(true);
      } catch (err) {
        setInvoicesData([]);
        setInvoicesAvailable(false);
      }
    } catch (err) {
      setError(err.message || "Failed to load payments dashboard.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    setPlansAttempted(true);
    try {
      const data = await getPlans();
      setPlans(Array.isArray(data) ? data : []);
      setPlansAvailable(true);
    } catch (err) {
      setPlans([]);
      setPlansAvailable(false);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const loadDisputes = useCallback(async () => {
    setDisputesLoading(true);
    setDisputesAttempted(true);
    setDisputesError("");
    try {
      const data = await getDisputes();
      setDisputes(Array.isArray(data) ? data : []);
    } catch (err) {
      setDisputes([]);
      setDisputesError(err.message || "Failed to load disputes.");
    } finally {
      setDisputesLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError("");
    try {
      const payload = buildAnalyticsPayload(analyticsFilters);
      const data = await getAnalyticsOverview(payload);
      setAnalyticsData(data || null);
    } catch (err) {
      setAnalyticsData(null);
      setAnalyticsError(err.message || "Failed to load analytics.");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [analyticsFilters]);

  const handleRefresh = useCallback(() => {
    if (isAnalytics) {
      loadAnalytics();
      return;
    }
    loadDashboard();
    if (
      currentPath.startsWith("/payments/subscriptions/plans") ||
      currentPath === "/payments/subscriptions/new" ||
      currentPath.startsWith("/payments/subscriptions/checkout")
    ) {
      loadPlans();
    }
    if (currentPath.startsWith("/payments/subscriptions/disputes")) {
      loadDisputes();
    }
  }, [currentPath, isAnalytics, loadAnalytics, loadDashboard, loadDisputes, loadPlans]);

  const updateAnalyticsDraft = useCallback((field, value) => {
    setAnalyticsDraftFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const applyAnalyticsFilters = useCallback(() => {
    setAnalyticsFilters(analyticsDraftFilters);
  }, [analyticsDraftFilters]);

  useEffect(() => {
    if (isProfileRoute) return;
    loadDashboard();
  }, [isProfileRoute, loadDashboard]);

  useEffect(() => {
    if (!isAnalytics) return;
    loadAnalytics();
  }, [isAnalytics, loadAnalytics]);

  useEffect(() => {
    if (!isAnalytics) return;
    setAnalyticsDraftFilters(analyticsFilters);
  }, [analyticsFilters, isAnalytics]);

  const dashboardData = useMemo(
    () => buildDashboardData(payments, subscriptions, subscriptionInvoices),
    [payments, subscriptions, subscriptionInvoices]
  );

  const normalizedSearch = (isOverview ? searchValue : "").trim().toLowerCase();
  const rangeDays = isOverview || isReports ? rangeOptions[rangeValue] : null;
  const cutoff = rangeDays ? Date.now() - rangeDays * 24 * 60 * 60 * 1000 : null;

  const matchesSearch = (value) => {
    if (!normalizedSearch) return true;
    return String(value || "").toLowerCase().includes(normalizedSearch);
  };

  const withinRange = (timestamp) => {
    if (!cutoff || !timestamp) return true;
    return timestamp >= cutoff;
  };

  const invoices = useMemo(() => {
    const derived = [];
    if (!invoicesAvailable) {
      const map = new Map();
      payments.forEach((payment) => {
        if (payment.invoice?.id && !map.has(payment.invoice.id)) {
          map.set(payment.invoice.id, payment.invoice);
        }
      });
      derived.push(...map.values());
    }
    const base = invoicesAvailable ? invoicesData : derived;
    return Array.from(base).sort((a, b) => {
      const left = getTimestamp(a.createdDate) || 0;
      const right = getTimestamp(b.createdDate) || 0;
      return right - left;
    });
  }, [invoicesAvailable, invoicesData, payments]);

  const paymentInvoices = useMemo(() => {
    const paidMap = new Map();
    const refundedMap = new Map();

    payments.forEach((payment) => {
      const invoice = payment.invoice;
      if (!invoice) return;
      if (payment.status === "SUCCESS") {
        paidMap.set(invoice.id, invoice);
      }
      if (payment.status === "REFUNDED") {
        refundedMap.set(invoice.id, { ...invoice, status: "REFUNDED" });
      }
    });

    return {
      paidInvoices: Array.from(paidMap.values()),
      refundedInvoices: Array.from(refundedMap.values()),
    };
  }, [payments]);

  const filteredPayments = payments.filter((payment) => {
    const timestamp = getTimestamp(payment.createdAt);
    return (
      withinRange(timestamp) &&
      (matchesSearch(payment.invoice?.invoiceNumber) ||
        matchesSearch(payment.invoice?.customerEmail) ||
        matchesSearch(payment.payerEmail) ||
        matchesSearch(payment.transactionId))
    );
  });

  const paidInvoicePayments = filteredPayments.filter((payment) => payment.status === "SUCCESS");

  const filteredSubscriptions = subscriptions.filter((subscription) => {
    const timestamp = getTimestamp(subscription.updatedAt || subscription.createdAt);
    return (
      withinRange(timestamp) &&
      (matchesSearch(subscription.stripeSubscriptionId) ||
        matchesSearch(subscription.customer?.email) ||
        matchesSearch(subscription.customer?.stripeCustomerId))
    );
  });

  const reportSubscriptions = useMemo(() => {
    if (reportSubscriptionStatus === "all") return filteredSubscriptions;
    return filteredSubscriptions.filter(
      (subscription) => String(subscription.status || "").toUpperCase() === reportSubscriptionStatus
    );
  }, [filteredSubscriptions, reportSubscriptionStatus]);

  const filteredSubscriptionInvoices = subscriptionInvoices.filter((invoice) => {
    const timestamp = getTimestamp(invoice.paidDate || invoice.createdDate);
    return (
      withinRange(timestamp) &&
      (matchesSearch(invoice.stripeSubscriptionId) || matchesSearch(invoice.customerEmail))
    );
  });

  const paidSubscriptionInvoices = filteredSubscriptionInvoices.filter(
    (invoice) => String(invoice.status || "").toUpperCase() === "PAID"
  );

  const filteredSubscriptionRefunds = subscriptionInvoices.filter((invoice) => {
    const timestamp = getTimestamp(invoice.paidDate || invoice.createdDate);
    const status = String(invoice.status || "").toUpperCase();
    if (status !== "REFUNDED") return false;
    return (
      withinRange(timestamp) &&
      (matchesSearch(invoice.stripeSubscriptionId) || matchesSearch(invoice.customerEmail))
    );
  });

  const filteredInvoices = invoices.filter((invoice) => {
    const timestamp = getTimestamp(invoice.createdDate);
    return (
      withinRange(timestamp) &&
      (matchesSearch(invoice.invoiceNumber) || matchesSearch(invoice.customerEmail))
    );
  });

  const reportInvoices = useMemo(() => {
    if (reportInvoiceStatus === "all") return filteredInvoices;
    return filteredInvoices.filter(
      (invoice) => String(invoice.status || "").toUpperCase() === reportInvoiceStatus
    );
  }, [filteredInvoices, reportInvoiceStatus]);

  const filteredRefundedInvoices = paymentInvoices.refundedInvoices.filter((invoice) => {
    const timestamp = getTimestamp(invoice.createdDate);
    return (
      withinRange(timestamp) &&
      (matchesSearch(invoice.invoiceNumber) || matchesSearch(invoice.customerEmail))
    );
  });

  const unpaidInvoices = filteredInvoices.filter(
    (invoice) => String(invoice.status || "").toUpperCase() !== "PAID"
  );

  const revenuePoints = useMemo(() => {
    const points = [];
    payments.forEach((payment) => {
      if (payment.status !== "SUCCESS") return;
      points.push({
        timestamp: getTimestamp(payment.createdAt),
        value: payment.amount || 0,
      });
    });
    subscriptionInvoices.forEach((invoice) => {
      if (String(invoice.status || "").toUpperCase() !== "PAID") return;
      points.push({
        timestamp: getTimestamp(invoice.paidDate || invoice.createdDate),
        value: invoice.amountPaid || 0,
      });
    });
    return points;
  }, [payments, subscriptionInvoices]);

  const revenueSeries = useMemo(
    () => buildSeriesFromPoints(revenuePoints, rangeDays),
    [revenuePoints, rangeDays]
  );

  const revenueTotalAll = useMemo(
    () => revenuePoints.reduce((total, point) => total + point.value, 0),
    [revenuePoints]
  );

  const mrrPoints = useMemo(
    () =>
      subscriptions
        .filter((subscription) => subscription.status === "ACTIVE")
        .map((subscription) => ({
          timestamp: getTimestamp(subscription.createdAt),
          value: subscription.plan?.amount || 0,
        })),
    [subscriptions]
  );

  const mrrSeries = useMemo(
    () => buildSeriesFromPoints(mrrPoints, rangeDays),
    [mrrPoints, rangeDays]
  );

  const mrrTotal = useMemo(
    () => mrrSeries.reduce((total, value) => total + value, 0),
    [mrrSeries]
  );

  const taxSeries = useMemo(
    () => revenueSeries.map((value) => value * 0.1),
    [revenueSeries]
  );

  const taxTotal = useMemo(
    () => taxSeries.reduce((total, value) => total + value, 0),
    [taxSeries]
  );

  const disputesPoints = useMemo(
    () =>
      disputes.map((dispute) => ({
        timestamp: getTimestamp(dispute.createdAt),
        value: 1,
      })),
    [disputes]
  );

  const disputesSeries = useMemo(
    () => buildSeriesFromPoints(disputesPoints, rangeDays),
    [disputesPoints, rangeDays]
  );

  const disputesTotal = useMemo(() => disputes.length, [disputes]);

  const filteredPlans = plans.filter((plan) =>
    matchesSearch(plan.name) || matchesSearch(plan.description)
  );

  const invoiceStatusOptions = useMemo(() => {
    const statuses = new Set();
    invoices.forEach((invoice) => {
      const status = String(invoice.status || "").toUpperCase();
      if (status) statuses.add(status);
    });
    return Array.from(statuses).sort();
  }, [invoices]);

  const subscriptionStatusOptions = useMemo(() => {
    const statuses = new Set();
    subscriptions.forEach((subscription) => {
      const status = String(subscription.status || "").toUpperCase();
      if (status) statuses.add(status);
    });
    return Array.from(statuses).sort();
  }, [subscriptions]);

  const customers = useMemo(() => {
    const map = new Map();
    subscriptions.forEach((subscription) => {
      const customer = subscription.customer;
      if (!customer) return;
      const key = customer.id || customer.email;
      if (!key) return;
      const existing = map.get(key) || {
        ...customer,
        subscriptionCount: 0,
        activeCount: 0,
        canceledCount: 0,
      };
      existing.subscriptionCount += 1;
      if (subscription.status === "ACTIVE") {
        existing.activeCount += 1;
      }
      if (subscription.status === "CANCELED") {
        existing.canceledCount += 1;
      }
      existing.updatedAt = subscription.updatedAt || subscription.createdAt || existing.updatedAt;
      map.set(key, existing);
    });
    return Array.from(map.values());
  }, [subscriptions]);

  const filteredCustomers = customers.filter((customer) => {
    const timestamp = getTimestamp(customer.updatedAt);
    return (
      withinRange(timestamp) &&
      (matchesSearch(customer.name) ||
        matchesSearch(customer.email) ||
        matchesSearch(customer.stripeCustomerId))
    );
  });

  const filteredActivity = dashboardData.activity.filter((item) => {
    const values = [
      item.meta?.invoiceNumber,
      item.meta?.customerEmail,
      item.meta?.stripeSubscriptionId,
      item.meta?.stripeCustomerId,
      item.meta?.transactionId,
    ];
    return withinRange(item.timestamp) && values.some(matchesSearch);
  });

  const kpiItems = [
    {
      key: "invoiceRevenue",
      label: "Invoice revenue",
      value: formatCurrency(dashboardData.kpis.invoiceRevenue),
      icon: FiDollarSign,
    },
    {
      key: "refundedAmount",
      label: "Refunded",
      value: formatCurrency(dashboardData.kpis.refundedAmount),
      icon: FiRepeat,
    },
    {
      key: "activeSubscriptions",
      label: "Active subscriptions",
      value: dashboardData.kpis.activeSubscriptions,
      icon: FiUserCheck,
    },
    {
      key: "canceledSubscriptions",
      label: "Canceled subscriptions",
      value: dashboardData.kpis.canceledSubscriptions,
      icon: FiXCircle,
    },
    {
      key: "mrr",
      label: "MRR (v1)",
      value: formatCurrency(dashboardData.kpis.mrr),
      helperText: `Paid subs: ${formatCurrency(dashboardData.kpis.subscriptionRevenue)}`,
      tooltip: `Subscription revenue: ${formatCurrency(dashboardData.kpis.subscriptionRevenue)}`,
      icon: FiTrendingUp,
    },
  ];

  const handleCreateInvoice = async (payload) => {
    setIsCreatingInvoice(true);
    try {
      await createInvoice(payload);
      toast({ title: "Invoice created", status: "success" });
      await loadDashboard();
      return true;
    } catch (err) {
      toast({ title: "Failed to create invoice", description: err.message, status: "error" });
      return false;
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  const handleCreatePlan = async (payload) => {
    setIsCreatingPlan(true);
    try {
      const created = await createPlan(payload);
      toast({ title: "Plan created", status: "success" });
      setPlans((prev) => [created, ...prev]);
      setPlansAvailable(true);
      return true;
    } catch (err) {
      toast({ title: "Failed to create plan", description: err.message, status: "error" });
      return false;
    } finally {
      setIsCreatingPlan(false);
    }
  };

  const handleCreateSubscription = async (payload) => {
    setIsCreatingSubscription(true);
    try {
      await createSubscription(payload);
      toast({ title: "Subscription created", status: "success" });
      await loadDashboard();
      return true;
    } catch (err) {
      toast({ title: "Failed to create subscription", description: err.message, status: "error" });
      return false;
    } finally {
      setIsCreatingSubscription(false);
    }
  };

  const handleCancelClick = (subscription) => {
    setSelectedSubscription(subscription);
    cancelDialog.onOpen();
  };

  const handleDeletePlanClick = (plan) => {
    setSelectedPlan(plan);
    deletePlanDialog.onOpen();
  };

  const handleConfirmDeletePlan = async () => {
    if (!selectedPlan) return;
    setIsDeletingPlan(true);
    try {
      await deletePlan(selectedPlan.id);
      toast({ title: "Plan deleted", status: "success" });
      setPlans((prev) => prev.filter((plan) => plan.id !== selectedPlan.id));
      deletePlanDialog.onClose();
      setSelectedPlan(null);
    } catch (err) {
      toast({ title: "Delete failed", description: err.message, status: "error" });
    } finally {
      setIsDeletingPlan(false);
    }
  };

  const handleRefundClick = (payload, type) => {
    setRefundTarget({ type, payload });
    setRefundError(null);
    refundDialog.onOpen();
  };

  const handleDisputeResponseClick = (dispute) => {
    setSelectedDispute(dispute);
    disputeDialog.onOpen();
  };

  const handleSubmitDisputeResponse = async (payload) => {
    setIsSubmittingDispute(true);
    try {
      await submitDisputeEvidence(payload);
      toast({ title: "Dispute response submitted", status: "success" });
      await loadDisputes();
      setSelectedDispute(null);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || "Submit failed." };
    } finally {
      setIsSubmittingDispute(false);
    }
  };

  const handleGenerateCheckout = async (invoice) => {
    setIsGeneratingCheckout(true);
    try {
      const payload = {
        invoiceId: invoice.id,
        successUrl: checkoutSuccessUrl,
        cancelUrl: checkoutCancelUrl,
      };
      const session = await createCheckoutSession(payload);
      setCheckoutSession(session);
      setCheckoutTarget({
        type: "invoice",
        subtitle: `Copy the checkout link for invoice ${invoice.invoiceNumber || invoice.id || ""}.`,
      });
      checkoutDialog.onOpen();
    } catch (err) {
      toast({ title: "Checkout failed", description: err.message, status: "error" });
    } finally {
      setIsGeneratingCheckout(false);
    }
  };

  const handleGenerateSubscriptionCheckout = async (payload) => {
    setIsGeneratingCheckout(true);
    try {
      const session = await createSubscriptionCheckoutSession(payload);
      const matchedPlan = plans.find(
        (plan) => String(plan.id) === String(payload.planId)
      );
      const customerLabel = payload.customerEmail || payload.customerName || "customer";
      const planLabel = matchedPlan?.name ? ` - ${matchedPlan.name}` : "";
      setCheckoutSession(session);
      setCheckoutTarget({
        type: "subscription",
        subtitle: `Copy the checkout link for ${customerLabel}${planLabel}.`,
      });
      checkoutDialog.onOpen();
      return true;
    } catch (err) {
      toast({ title: "Checkout failed", description: err.message, status: "error" });
      return false;
    } finally {
      setIsGeneratingCheckout(false);
    }
  };

  const copyCheckoutLink = async () => {
    const url = checkoutSession?.url;
    if (!url) return false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return true;
      }
    } catch (error) {
      // Fallback below.
    }
    try {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      return copied;
    } catch (error) {
      return false;
    }
  };

  const handleSendCheckoutLink = async () => {
    setIsSendingCheckout(true);
    const success = await copyCheckoutLink();
    if (success) {
      toast({ title: "Checkout link copied", status: "success" });
      checkoutDialog.onClose();
    } else {
      toast({ title: "Copy failed", description: "Copy the link manually.", status: "error" });
    }
    setIsSendingCheckout(false);
  };

  const handlePrintSubscriptions = () => {
    if (typeof window === "undefined") return;
    const rowsHtml = reportSubscriptions
      .map((subscription) => {
        const customer = escapeHtml(subscription.customer?.email || "-");
        const plan = escapeHtml(subscription.plan?.name || "-");
        const amount = escapeHtml(
          formatCurrency(subscription.plan?.amount, subscription.plan?.currency)
        );
        const status = escapeHtml(subscription.status || "-");
        const periodEnd = escapeHtml(formatDateTime(subscription.currentPeriodEnd));
        const created = escapeHtml(formatDateTime(subscription.createdAt));
        return `<tr><td>${customer}</td><td>${plan}</td><td style="text-align:right">${amount}</td><td>${status}</td><td>${periodEnd}</td><td>${created}</td></tr>`;
      })
      .join("");

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Subscriptions Report</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #1a202c; }
      h2 { margin: 0 0 8px; }
      p { margin: 0 0 16px; color: #4a5568; font-size: 12px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }
      th { background: #f7fafc; }
    </style>
  </head>
  <body>
    <h2>Subscriptions Report</h2>
    <p>Generated: ${escapeHtml(new Date().toLocaleString())}</p>
    <table>
      <thead>
        <tr>
          <th>Customer</th>
          <th>Plan</th>
          <th style="text-align:right">Amount</th>
          <th>Status</th>
          <th>Period End</th>
          <th>Created</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  </body>
</html>`;

    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) {
      toast({ title: "Print blocked", description: "Allow popups to print.", status: "error" });
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handlePrintInvoices = () => {
    if (typeof window === "undefined") return;
    const rowsHtml = reportInvoices
      .map((invoice) => {
        const invoiceNumber = escapeHtml(invoice.invoiceNumber || `#${invoice.id}`);
        const customer = escapeHtml(invoice.customerEmail || "-");
        const amount = escapeHtml(formatCurrency(invoice.amount, invoice.currency));
        const status = escapeHtml(invoice.status || "-");
        const created = escapeHtml(formatDateTime(invoice.createdDate));
        const due = escapeHtml(formatDateTime(invoice.dueDate));
        const paid = escapeHtml(formatDateTime(invoice.paidDate));
        return `<tr><td>${invoiceNumber}</td><td>${customer}</td><td style="text-align:right">${amount}</td><td>${status}</td><td>${created}</td><td>${due}</td><td>${paid}</td></tr>`;
      })
      .join("");

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Invoices Report</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #1a202c; }
      h2 { margin: 0 0 8px; }
      p { margin: 0 0 16px; color: #4a5568; font-size: 12px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }
      th { background: #f7fafc; }
    </style>
  </head>
  <body>
    <h2>Invoices Report</h2>
    <p>Generated: ${escapeHtml(new Date().toLocaleString())}</p>
    <table>
      <thead>
        <tr>
          <th>Invoice</th>
          <th>Customer</th>
          <th style="text-align:right">Amount</th>
          <th>Status</th>
          <th>Created</th>
          <th>Due</th>
          <th>Paid</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  </body>
</html>`;

    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) {
      toast({ title: "Print blocked", description: "Allow popups to print.", status: "error" });
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleConfirmRefund = async () => {
    if (!refundTarget?.payload) return;
    setIsRefunding(true);
    try {
      await refundPayment(refundTarget.payload.id);
      toast({ title: "Payment refunded", status: "success" });
      await loadDashboard();
      refundDialog.onClose();
      setRefundTarget(null);
      setRefundError(null);
    } catch (err) {
      const rawMessage = err.message || "";
      const rawDetails = err.details || "";
      const combined = `${rawMessage}\n${rawDetails}`.toLowerCase();
      let title = "Refund failed";
      let summary = "Refund failed. See details for the full error.";
      if (combined.includes("already been refunded") || combined.includes("charge_already_refunded")) {
        title = "Already refunded";
        summary = "This payment was already refunded in Stripe.";
      }
      const detailsText = rawDetails || rawMessage;
      setRefundError({ title, summary, details: detailsText });
    } finally {
      setIsRefunding(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!selectedSubscription) return;
    setIsCanceling(true);
    try {
      await cancelSubscription(selectedSubscription.id);
      toast({ title: "Subscription canceled", status: "success" });
      await loadDashboard();
      cancelDialog.onClose();
    } catch (err) {
      toast({ title: "Cancel failed", description: err.message, status: "error" });
    } finally {
      setIsCanceling(false);
    }
  };

  useEffect(() => {
    const needsPlans =
      currentPath.startsWith("/payments/subscriptions/plans") ||
      currentPath === "/payments/subscriptions/new" ||
      currentPath.startsWith("/payments/subscriptions/checkout");
    if (needsPlans && !plansAttempted) {
      loadPlans();
    }
  }, [currentPath, loadPlans, plansAttempted]);

  useEffect(() => {
    if (
      (currentPath.startsWith("/payments/subscriptions/disputes") ||
        currentPath.startsWith("/payments/reports/disputes")) &&
      !disputesAttempted
    ) {
      loadDisputes();
    }
  }, [currentPath, loadDisputes, disputesAttempted]);

  const viewKey = useMemo(() => {
    if (currentPath.startsWith("/payments/invoices/new")) return "invoiceCreate";
    if (currentPath.startsWith("/payments/invoices/checkout")) return "invoiceCheckout";
    if (currentPath.startsWith("/payments/invoices/refunds")) return "refunds";
    if (currentPath.startsWith("/payments/invoices/payments")) return "payments";
    if (currentPath.startsWith("/payments/invoices")) return "invoices";
    if (currentPath.startsWith("/payments/subscriptions/new-plan")) return "subscriptionCreatePlan";
    if (currentPath.startsWith("/payments/subscriptions/checkout")) return "subscriptionCheckout";
    if (currentPath.startsWith("/payments/subscriptions/new")) return "subscriptionCreate";
    if (currentPath.startsWith("/payments/subscriptions/plans")) return "subscriptionPlans";
    if (currentPath.startsWith("/payments/subscriptions/customers")) return "subscriptionCustomers";
    if (currentPath.startsWith("/payments/subscriptions/invoices/refunds")) return "subscriptionRefunds";
    if (currentPath.startsWith("/payments/subscriptions/invoices")) return "subscriptionPayments";
    if (currentPath.startsWith("/payments/subscriptions/disputes")) return "subscriptionDisputes";
    if (currentPath.startsWith("/payments/subscriptions")) return "subscriptions";
    if (currentPath.startsWith("/payments/analytics")) return "analytics";
    if (currentPath.startsWith("/payments/reports/revenue")) return "reportsRevenue";
    if (currentPath.startsWith("/payments/reports/mrr-churn")) return "reportsMrr";
    if (currentPath.startsWith("/payments/reports/disputes")) return "reportsDisputes";
    if (currentPath.startsWith("/payments/reports/invoices")) return "reportsInvoices";
    if (currentPath.startsWith("/payments/reports/subscriptions")) return "reportsSubscriptions";
    if (currentPath.startsWith("/payments/reports/taxes")) return "reportsTaxes";
    if (currentPath.startsWith("/payments/reports")) return "reports";
    if (currentPath.startsWith("/payments/settings")) return "settings";
    if (currentPath.startsWith("/payments/audit-log")) return "audit";
    if (currentPath.startsWith("/payments/help")) return "help";
    if (currentPath.startsWith("/payments/status")) return "status";
    if (currentPath.startsWith("/payments/profile")) return "profile";
    return "overview";
  }, [currentPath]);

  const pageMeta = {
    overview: {
      title: "Payments Dashboard",
      subtitle: "Track invoice revenue, subscription health, and charge activity.",
    },
    invoiceCreate: {
      title: "Create Invoice",
      subtitle: "Issue a new invoice for your customer.",
    },
    invoiceCheckout: {
      title: "Stripe Checkout",
      subtitle: "Generate one-time checkout links for unpaid invoices.",
    },
    invoices: {
      title: "All Invoices",
      subtitle: "Review all invoices and their status.",
    },
    payments: {
      title: "Payments",
      subtitle: "Invoices with successful payment status.",
    },
    refunds: {
      title: "Refunds",
      subtitle: "Invoices with refunded payment status.",
    },
    subscriptionCreatePlan: {
      title: "Create Plan",
      subtitle: "Set up a new subscription plan.",
    },
    subscriptionCreate: {
      title: "Create Subscription",
      subtitle: "Start a subscription for a customer.",
    },
    subscriptionCheckout: {
      title: "Subscription Checkout",
      subtitle: "Generate Stripe Checkout links for new subscriptions.",
    },
    subscriptions: {
      title: "All Subscriptions",
      subtitle: "Monitor active and canceled subscriptions.",
    },
    subscriptionPlans: {
      title: "All Plans",
      subtitle: "Manage recurring subscription plans.",
    },
    subscriptionCustomers: {
      title: "Subscription Customers",
      subtitle: "View and filter customers tied to subscriptions.",
    },
    subscriptionPayments: {
      title: "Subscription Payments",
      subtitle: "Review Stripe subscription invoices.",
    },
    subscriptionRefunds: {
      title: "Subscription Refunds",
      subtitle: "Invoices with refunded status.",
    },
    subscriptionDisputes: {
      title: "Disputes",
      subtitle: "Review and respond to disputed payments.",
    },
    analytics: {
      title: "Analytics",
      subtitle: "Unified analytics across payments, invoices, and subscriptions.",
    },
    reports: {
      title: "Reports",
      subtitle: "Revenue and subscription analytics.",
    },
    reportsRevenue: {
      title: "Revenue Report",
      subtitle: "Invoice and subscription revenue rollup.",
    },
    reportsMrr: {
      title: "MRR / Churn",
      subtitle: "Track recurring revenue trends.",
    },
    reportsDisputes: {
      title: "Disputes",
      subtitle: "Monitor disputes and chargebacks.",
    },
    reportsInvoices: {
      title: "All Invoices",
      subtitle: "Filter invoices by date range and status.",
    },
    reportsSubscriptions: {
      title: "All Subscriptions",
      subtitle: "Filter subscriptions by date range and status.",
    },
    reportsTaxes: {
      title: "Taxes",
      subtitle: "Tax reporting for payments.",
    },
    settings: {
      title: "Settings",
      subtitle: "Configure payment settings.",
    },
    audit: {
      title: "Audit Log",
      subtitle: "Review administrative actions.",
    },
    help: {
      title: "Payments Help",
      subtitle: "Learn how to use every payments feature.",
    },
    status: {
      title: "Status",
      subtitle: "System health and webhook status.",
    },
    profile: {
      title: "Profile",
      subtitle: "Manage your profile and preferences.",
    },
  };

  const { title, subtitle } = pageMeta[viewKey] || pageMeta.overview;

  const refundDetails = useMemo(() => {
    if (!refundTarget) return null;
    if (refundTarget.type === "invoice") {
      const payment = refundTarget.payload;
      return {
        title: "Refund Payment",
        subtitle: `Refund payment for invoice ${payment.invoice?.invoiceNumber || payment.invoice?.id || payment.id}?`,
        amount: formatCurrency(payment.amount, payment.currency),
        methodLabel: payment.paymentMethod || "Stripe",
        methodDetail: payment.transactionId || payment.paymentMethod || "-",
      };
    }
    if (refundTarget.type === "dispute") {
      const dispute = refundTarget.payload;
      return {
        title: "Refund Payment",
        subtitle: `Refund disputed payment ${dispute.transactionId || dispute.id}?`,
        amount: formatCurrency(dispute.amount, dispute.currency),
        methodLabel: dispute.paymentMethod || "Stripe",
        methodDetail: dispute.transactionId || "-",
      };
    }
    const invoice = refundTarget.payload;
    return {
      title: "Refund Payment",
      subtitle: `Refund payment for subscription invoice ${invoice.externalInvoiceId || invoice.id}?`,
      amount: formatCurrency(invoice.amountPaid || invoice.amountDue, invoice.currency),
      methodLabel: "Stripe",
      methodDetail: invoice.stripePaymentIntentId || invoice.stripeSubscriptionId || "-",
    };
  }, [refundTarget]);

  const checkoutSubtitle = checkoutTarget?.subtitle || "Copy the checkout link.";

  return (
    <Flex bg="gray.50" minH="100vh">
      <PaymentsSidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed((prev) => !prev)}
        currentPath={currentPath}
      />
      <Box flex="1" px={{ base: 4, lg: 8 }} py={8}>
        <DashboardHeader
          title={title}
          subtitle={subtitle}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          rangeValue={rangeValue}
          onRangeChange={setRangeValue}
          onRefresh={handleRefresh}
          onOpenCreateInvoice={() => navigate("/payments/invoices/new")}
          onOpenCreatePlan={() => navigate("/payments/subscriptions/new-plan")}
          onOpenCreateSubscription={() => navigate("/payments/subscriptions/new")}
          showFilters={isOverview || isReports}
          showSearch={isOverview}
          showCreate={false}
        />

        {error && !isProfileRoute ? (
          <ErrorState title="Dashboard unavailable" description={error} onRetry={loadDashboard} />
        ) : viewKey === "overview" ? (
          <>
            <KpiGrid items={kpiItems} isLoading={isLoading} />
            <Flex direction={{ base: "column", lg: "row" }} gap={6} mb={6}>
              <Box flex="1.2">
                <ActivityFeed
                  items={filteredActivity}
                  isLoading={isLoading}
                  formatDateTime={formatDateTime}
                  statusColor={statusColor}
                />
              </Box>
              <Box flex="1">
                <Flex direction="column" gap={6}>
                  <AlertsPanel alerts={dashboardData.alerts} />
                  <Card variant="outline" _hover={{ boxShadow: "md" }} transition="box-shadow 0.2s ease">
                    <CardHeader>
                      <HStack justify="space-between">
                        <Heading size="sm">Revenue Trend</Heading>
                        <FiBarChart2 />
                      </HStack>
                    </CardHeader>
                    <CardBody>
                      {isLoading ? (
                        <LoadingState />
                      ) : (
                        <Box>
                          <Text color="gray.600">
                            Chart placeholder. Hook in Stripe analytics or custom reporting later.
                          </Text>
                          <HStack mt={4} spacing={3}>
                            <FiShield />
                            <Text fontSize="sm" color="gray.500">
                              Future-proof slot for charts.
                            </Text>
                          </HStack>
                        </Box>
                      )}
                    </CardBody>
                  </Card>
                </Flex>
              </Box>
            </Flex>

          </>
        ) : viewKey === "invoiceCreate" ? (
          <CreateInvoicePanel
            onSubmit={handleCreateInvoice}
            isSubmitting={isCreatingInvoice}
          />
        ) : viewKey === "invoiceCheckout" ? (
          <StripeCheckoutTable
            invoices={unpaidInvoices}
            isLoading={isLoading}
            isGenerating={isGeneratingCheckout}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
            onGenerate={handleGenerateCheckout}
          />
        ) : viewKey === "invoices" ? (
          <InvoicesTable
            invoices={filteredInvoices}
            isLoading={isLoading}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
          />
        ) : viewKey === "payments" ? (
          <InvoicePaymentsTable
            payments={paidInvoicePayments}
            onRefund={(payment) => handleRefundClick(payment, "invoice")}
            isLoading={isLoading}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
          />
        ) : viewKey === "refunds" ? (
          <InvoicesTable
            invoices={filteredRefundedInvoices}
            isLoading={isLoading}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
          />
        ) : viewKey === "subscriptionCreatePlan" ? (
          <CreatePlanPanel onSubmit={handleCreatePlan} isSubmitting={isCreatingPlan} />
        ) : viewKey === "subscriptionCreate" ? (
          <CreateSubscriptionPanel
            onSubmit={handleCreateSubscription}
            plans={plans}
            plansAvailable={plansAvailable}
            plansLoading={plansLoading}
            onRefreshPlans={loadPlans}
            isSubmitting={isCreatingSubscription}
          />
        ) : viewKey === "subscriptionCheckout" ? (
          <SubscriptionCheckoutPanel
            onSubmit={handleGenerateSubscriptionCheckout}
            plans={plans}
            plansAvailable={plansAvailable}
            plansLoading={plansLoading}
            onRefreshPlans={loadPlans}
            isSubmitting={isGeneratingCheckout}
          />
        ) : viewKey === "subscriptions" ? (
          <SubscriptionsTable
            subscriptions={filteredSubscriptions}
            onCancel={handleCancelClick}
            isLoading={isLoading}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
          />
        ) : viewKey === "subscriptionPlans" ? (
          <PlansTable
            plans={filteredPlans}
            isLoading={plansLoading}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
            hasAccess={plansAvailable}
            onRetry={loadPlans}
            onDelete={handleDeletePlanClick}
          />
        ) : viewKey === "subscriptionCustomers" ? (
          <CustomersTable
            customers={filteredCustomers}
            isLoading={isLoading}
            formatDateTime={formatDateTime}
          />
        ) : viewKey === "subscriptionPayments" ? (
          <SubscriptionInvoicesTable
            invoices={paidSubscriptionInvoices}
            isLoading={isLoading}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
            onRefund={(invoice) => handleRefundClick(invoice, "subscription")}
          />
        ) : viewKey === "subscriptionRefunds" ? (
          <SubscriptionInvoicesTable
            invoices={filteredSubscriptionRefunds}
            isLoading={isLoading}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
          />
        ) : viewKey === "subscriptionDisputes" ? (
          disputesError ? (
            <ErrorState
              title="Disputes unavailable"
              description={disputesError}
              onRetry={loadDisputes}
            />
          ) : (
            <DisputesTable
              disputes={disputes}
              isLoading={disputesLoading}
              isSubmitting={isRefunding}
              formatCurrency={formatCurrency}
              formatDateTime={formatDateTime}
              statusColor={statusColor}
              onRespond={handleDisputeResponseClick}
              onRefund={(dispute) => handleRefundClick(dispute, "dispute")}
            />
          )
        ) : viewKey === "analytics" ? (
          <AnalyticsOverviewPanel
            data={analyticsData}
            isLoading={analyticsLoading}
            error={analyticsError}
            onRetry={loadAnalytics}
            filters={analyticsDraftFilters}
            onFilterChange={updateAnalyticsDraft}
            onApply={applyAnalyticsFilters}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
          />
        ) : viewKey === "reportsRevenue" ? (
          <ReportChartCard
            title="Revenue"
            total={formatCurrency(revenueTotalAll)}
            description="All-time paid payments. Chart reflects the selected range."
            series={revenueSeries}
            colorScheme="blue"
          />
        ) : viewKey === "reportsMrr" ? (
          <ReportChartCard
            title="MRR (Approx)"
            total={formatCurrency(mrrTotal)}
            description="Active plan amounts created within the selected range."
            series={mrrSeries}
            colorScheme="green"
          />
        ) : viewKey === "reportsDisputes" ? (
          <ReportChartCard
            title="Disputes"
            total={`${disputesTotal}`}
            description="Total disputed payments to date. Chart reflects the selected range."
            series={disputesSeries}
            colorScheme="red"
          />
        ) : viewKey === "reportsInvoices" ? (
          <ReportInvoicesPanel
            invoices={reportInvoices}
            isLoading={isLoading}
            statusOptions={invoiceStatusOptions}
            statusFilter={reportInvoiceStatus}
            onStatusChange={setReportInvoiceStatus}
            onPrint={handlePrintInvoices}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
          />
        ) : viewKey === "reportsSubscriptions" ? (
          <ReportSubscriptionsPanel
            subscriptions={reportSubscriptions}
            isLoading={isLoading}
            statusOptions={subscriptionStatusOptions}
            statusFilter={reportSubscriptionStatus}
            onStatusChange={setReportSubscriptionStatus}
            onPrint={handlePrintSubscriptions}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            statusColor={statusColor}
          />
        ) : viewKey === "reportsTaxes" ? (
          <ReportChartCard
            title="Estimated Taxes"
            total={formatCurrency(taxTotal)}
            description="Estimated at 10% of revenue for the selected range."
            series={taxSeries}
            colorScheme="orange"
          />
        ) : viewKey === "reports" ? (
          <Tabs variant="enclosed" colorScheme="blue">
            <TabList>
              <Tab>Revenue</Tab>
              <Tab>MRR</Tab>
              <Tab>Taxes</Tab>
              <Tab>All Invoices</Tab>
              <Tab>All Subscriptions</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <ReportChartCard
                  title="Revenue"
                  total={formatCurrency(revenueTotalAll)}
                  description="All-time paid payments. Chart reflects the selected range."
                  series={revenueSeries}
                  colorScheme="blue"
                />
              </TabPanel>
              <TabPanel px={0}>
                <ReportChartCard
                  title="MRR (Approx)"
                  total={formatCurrency(mrrTotal)}
                  description="Active plan amounts created within the selected range."
                  series={mrrSeries}
                  colorScheme="green"
                />
              </TabPanel>
              <TabPanel px={0}>
                <ReportChartCard
                  title="Estimated Taxes"
                  total={formatCurrency(taxTotal)}
                  description="Estimated at 10% of revenue for the selected range."
                  series={taxSeries}
                  colorScheme="orange"
                />
              </TabPanel>
              <TabPanel px={0}>
                <ReportInvoicesPanel
                  invoices={reportInvoices}
                  isLoading={isLoading}
                  statusOptions={invoiceStatusOptions}
                  statusFilter={reportInvoiceStatus}
                  onStatusChange={setReportInvoiceStatus}
                  formatCurrency={formatCurrency}
                  formatDateTime={formatDateTime}
                  statusColor={statusColor}
                />
              </TabPanel>
              <TabPanel px={0}>
                <ReportSubscriptionsPanel
                  subscriptions={reportSubscriptions}
                  isLoading={isLoading}
                  statusOptions={subscriptionStatusOptions}
                  statusFilter={reportSubscriptionStatus}
                  onStatusChange={setReportSubscriptionStatus}
                  onPrint={handlePrintSubscriptions}
                  formatCurrency={formatCurrency}
                  formatDateTime={formatDateTime}
                  statusColor={statusColor}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        ) : viewKey === "profile" ? (
          isClerkProvider ? (
            <Flex direction="column" gap={6}>
              <Card variant="outline">
                <CardHeader>
                  <Flex align="center" justify="space-between">
                    <Heading size="sm">Account actions</Heading>
                    <SignOutButton>
                      <Button size="sm" colorScheme="red" variant="outline">
                        Log out
                      </Button>
                    </SignOutButton>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <Text color="gray.600" fontSize="sm">
                    Manage your Clerk profile details and security settings.
                  </Text>
                </CardBody>
              </Card>
              <Box
                bg="white"
                borderWidth="1px"
                borderRadius="lg"
                overflow="hidden"
                p={{ base: 2, md: 4 }}
              >
                <UserProfile routing="path" path="/payments/profile" />
              </Box>
            </Flex>
          ) : (
            <PlaceholderPanel
              title={title}
              description="Profile management is available when using Clerk authentication."
              icon={FiUserCheck}
            />
          )
        ) : viewKey === "help" ? (
          <PaymentsHelpPanel />
        ) : (
          <PlaceholderPanel
            title={title}
            description="This section is ready for future customization."
            icon={FiShield}
          />
        )}

        <RefundDialog
          isOpen={refundDialog.isOpen}
          onClose={() => {
            refundDialog.onClose();
            setRefundTarget(null);
            setRefundError(null);
          }}
          onConfirm={handleConfirmRefund}
          isLoading={isRefunding}
          title={refundDetails?.title || "Refund Payment"}
          subtitle={refundDetails?.subtitle || "Refund this payment?"}
          amount={refundDetails?.amount || "-"}
          methodLabel={refundDetails?.methodLabel || "Stripe"}
          methodDetail={refundDetails?.methodDetail || "-"}
          error={refundError}
        />

        <CheckoutLinkDialog
          isOpen={checkoutDialog.isOpen}
          onClose={() => {
            checkoutDialog.onClose();
            setCheckoutSession(null);
            setCheckoutTarget(null);
          }}
          onSend={handleSendCheckoutLink}
          onCopy={copyCheckoutLink}
          isSending={isSendingCheckout}
          title="Stripe Checkout Link"
          subtitle={checkoutSubtitle}
          url={checkoutSession?.url || ""}
        />

        <DisputeResponseDialog
          isOpen={disputeDialog.isOpen}
          onClose={() => {
            disputeDialog.onClose();
            setSelectedDispute(null);
          }}
          dispute={selectedDispute}
          onSubmit={handleSubmitDisputeResponse}
          isSubmitting={isSubmittingDispute}
        />

        <ConfirmDialog
          isOpen={deletePlanDialog.isOpen}
          onClose={() => {
            deletePlanDialog.onClose();
            setSelectedPlan(null);
          }}
          onConfirm={handleConfirmDeletePlan}
          isLoading={isDeletingPlan}
          title="Deactivate plan"
          description={`Deactivate plan ${selectedPlan?.name || ""}? This cannot be undone.`}
          confirmLabel="Deactivate plan"
          confirmColorScheme="red"
        />

        <ConfirmDialog
          isOpen={cancelDialog.isOpen}
          onClose={cancelDialog.onClose}
          onConfirm={handleConfirmCancel}
          isLoading={isCanceling}
          title="Cancel subscription"
          description={`Cancel subscription for ${selectedSubscription?.customer?.email || "this customer"}?`}
          confirmLabel="Cancel subscription"
          confirmColorScheme="orange"
        />
      </Box>
    </Flex>
  );
};

export default PaymentsDashboardPage;

